/**
 * Utility to refresh organizationId for already-logged-in users
 * 
 * IMPORTANT: This utility can only refresh organizationId if it's already available
 * from the user management service. It cannot search across all organization tables
 * because that would require trying unlimited organizationIds.
 * 
 * If organizationId is missing, the user must log out and log back in so that
 * the user management service can provide organizationId in the login response.
 */

import { getDoctorByUserId } from "../services/doctorService";
import { getStaffByUserId } from "../services/StaffService";
import { isPathModuleStaffRole } from "./pathRoleUtils";

/**
 * Refresh organizationId for the current logged-in user
 * 
 * This function attempts to get organizationId from staff/doctor table, but it
 * requires organizationId to already be in localStorage (from user management service)
 * because apiClient needs it to query the correct organization-specific table.
 * 
 * If organizationId is not in localStorage, this function cannot work because
 * we don't know which organization table to query.
 * 
 * @returns {Promise<number|null>} - The organizationId if found, null otherwise
 */
export const refreshOrganizationId = async () => {
  try {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      console.log("[organizationRefresh] No user found in localStorage");
      return null;
    }

    const user = JSON.parse(storedUser);
    const userId = user?.id;
    const roleName = user?.roleName?.toLowerCase();

    if (!userId) {
      console.log("[organizationRefresh] No userId found in user object");
      return null;
    }

    // Check if organizationId already exists
    if (user?.organizationId && Number(user.organizationId) > 0) {
      console.log(`[organizationRefresh] ✅ organizationId already set: ${user.organizationId}`);
      return Number(user.organizationId);
    }

    console.log(`[organizationRefresh] ⚠️ organizationId missing for user ${userId} (role: ${roleName})`);
    console.log(`[organizationRefresh] Attempting to fetch organizationId from staff/doctor table...`);
    console.log(`[organizationRefresh] Note: getStaffByUserId/getDoctorByUserId will call WITHOUT organizationId`);
    console.log(`[organizationRefresh] Backend should search across all organization tables to find the user`);
    
    // Try to fetch from staff/doctor table
    // getStaffByUserId/getDoctorByUserId now use axios directly (bypassing apiClient)
    // so they can call WITHOUT organizationId, allowing backend to search across all tables
    let organizationId = null;
    let userDetails = null;

    if (roleName === "doctor" || roleName === "surgeon") {
      console.log(`[organizationRefresh] Attempting to fetch doctor details for userId ${userId}...`);
      try {
        userDetails = await getDoctorByUserId(userId);
        console.log(`[organizationRefresh] Doctor details received:`, userDetails);
        
        // Check for organizationId in response (could be organizationId or organization_id)
        const orgId = userDetails?.organizationId || userDetails?.organization_id;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
          console.log(`[organizationRefresh] ✅ Found organizationId ${organizationId} from doctor table`);
        } else {
          console.warn(`[organizationRefresh] ⚠️ Doctor details found but no organizationId in response`);
          console.warn(`[organizationRefresh] Response keys:`, Object.keys(userDetails || {}));
        }
      } catch (error) {
        console.error("[organizationRefresh] ❌ Error fetching doctor details:", error);
        console.error("[organizationRefresh] Error details:", error.response?.data || error.message);
        console.error("[organizationRefresh] Backend should search across all organization tables when organizationId is not provided");
      }
    } else if (
      roleName === "staff" ||
      roleName === "admin" ||
      roleName === "superadmin" ||
      roleName === "frontdesk" ||
      roleName === "pharmacy admin" ||
      isPathModuleStaffRole(roleName)
    ) {
      console.log(`[organizationRefresh] Attempting to fetch staff details for userId ${userId} (role: ${roleName})...`);
      try {
        userDetails = await getStaffByUserId(userId);
        console.log(`[organizationRefresh] Staff details received:`, userDetails);
        
        // Check for organizationId in response (could be organizationId or organization_id)
        const orgId = userDetails?.organizationId || userDetails?.organization_id;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
          console.log(`[organizationRefresh] ✅ Found organizationId ${organizationId} from staff table`);
        } else {
          console.warn(`[organizationRefresh] ⚠️ Staff details found but no organizationId in response`);
          console.warn(`[organizationRefresh] Response keys:`, Object.keys(userDetails || {}));
          console.warn(`[organizationRefresh] Full response:`, userDetails);
        }
      } catch (error) {
        console.error("[organizationRefresh] ❌ Error fetching staff details:", error);
        console.error("[organizationRefresh] Error details:", error.response?.data || error.message);
        console.error("[organizationRefresh] Backend should search across all organization tables when organizationId is not provided");
      }
    } else {
      console.warn(`[organizationRefresh] ⚠️ Unknown role: ${roleName}. Cannot fetch organizationId.`);
    }

    // Update localStorage if organizationId was found
    if (organizationId) {
      const updatedUser = {
        ...user,
        organizationId: organizationId,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.setItem("organizationId", organizationId.toString());
      console.log(`[organizationRefresh] ✅ Updated localStorage with organizationId: ${organizationId}`);
      
      // Dispatch event to notify AuthContext and other components
      window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
        detail: { updatedUser } 
      }));
      console.log(`[organizationRefresh] ✅ Dispatched userOrganizationIdUpdated event`);
      
      return Number(organizationId);
    } else {
      console.warn(`[organizationRefresh] ⚠️ Could not find organizationId for user ${userId}`);
      console.warn(`[organizationRefresh] ⚠️ User must log out and log back in to get organizationId from user management service`);
      return null;
    }
  } catch (error) {
    console.error("[organizationRefresh] Error refreshing organizationId:", error);
    return null;
  }
};

